import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers';

// Styles
import styles from './DndListItem.module.css';

/**
 * DndListItem - Generic component for drag and drop list items
 * @template T - Type of the items (must have an id property)
 * @param {object} param0
 * @param {string} param0.name
 * @param {Array<T & {id: string}>} param0.items - Items to display and reorder
 * @param {(items: Array<T & {id: string}>) => React.ReactNode} param0.children - Render function receiving items array
 * @param {(items: Array<T & {id: string}>, oldIndex: number, newIndex: number) => void} [param0.handleSaveDndOrder] - Callback when items are reordered
 * @param {string} [param0.idKey = 'id'] - Key to use for the items id
 * @returns {JSX.Element} The Category component
 */
function DndListItem({
  name,
  items,
  handleSaveDndOrder = () => {},
  children,
  idKey = 'id',
}) {
  /**
   * DRAG AND DROP MANAGEMENT
   */
  /**
   * Drag and Drop sensors management (Pointer, Touch, Keyboard)
   */
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // distance of the pointer to the element before activation
      },
    }),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  /**
   * Function to manage the end of the drag and drop
   * @param {import('@dnd-kit/core').DragEndEvent} event - Event object
   */
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = items.findIndex((item) => {
        /** @type {any} */
        const itemAny = item;
        return itemAny[idKey] === active.id;
      });
      const newIndex = items.findIndex((item) => {
        /** @type {any} */
        const itemAny = item;
        return itemAny[idKey] === over?.id;
      });
      const newItems = arrayMove(items, oldIndex, newIndex);

      const newOrders = newItems.map((
        item /** @type {T & {id: string | number}} */,
        index /** @type {number} */,
      ) => ({
        ...item,
        position: index + 1,
      }));

      handleSaveDndOrder(newOrders, oldIndex, newIndex);
    }
  };

  /**
   * Function to enable the collision detection
   * @param {any} args - Collision detection function
   * @returns {any} - Collision detection function
   */
  const collisionDetectionEnable = (args) => closestCenter(args);

  return (
    <div className={styles.container}>
      <div className={styles['dnd-items']}>
        <div
          id={`${name}_container`}
          className={styles['dnd-items-container']}
        >
          <DndContext
            collisionDetection={collisionDetectionEnable}
            onDragEnd={handleDragEnd}
            sensors={sensors}
            modifiers={[restrictToFirstScrollableAncestor]}
          >
            <SortableContext
              items={items.map((item) => {
                /** @type {any} */
                const itemAny = item;
                return itemAny[idKey];
              })}
              strategy={rectSortingStrategy}
            >
              <div className={styles['dnd-list-card']}>
                {children(items)}
              </div>
            </SortableContext>
          </DndContext>

        </div>
      </div>
    </div>
  );
}

export default DndListItem;

/**
 *
 * @param {object} param0
 * @param {React.ReactNode} param0.children
 * @returns {JSX.Element} The CategoriesList component
 */
export function DndList({ children }) {
  return (
    <div className={styles['dnd-categories-container']}>
      <div className={styles['dnd-categories-list']}>
        {children}
      </div>
    </div>
  );
}

/**
 *
 * @param {object} param0
 * @param {React.ReactNode} param0.children
 * @returns {JSX.Element} The CategoryWrapper component
 */
export function DndWrapper({ children }) {
  return (
    <div className={styles['dnd-wrapper']}>
      {children}
    </div>
  );
}
